import { estimateTokenCostUsd } from "../../lib/connectorUsage";
import { defaultManagedAIProvider } from "../../../shared/aiProviders";
import {
  isManagedAiConnectorId,
  managedAiProviderFromConnectorId,
} from "../../../shared/aiProviders/managedAiConnector";
import type { ConnectorUsageDailyRow } from "../../hooks/useConnectorUsageDaily";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";

export type { ConnectorUsageDailyRow } from "../../hooks/useConnectorUsageDaily";

// The wide per-(connector, model) record produced by
// adaptConnectorUsageToAiRecords from the connector_usage_daily ledger.
export type AiUsageRecord = {
  source: string;
  provider: string;
  model: string;
  aiProviderId: string | null;
  connectorId: string | null;
  workflowId: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  cacheWriteTokens: number | null;
  requestCount: number | null;
  workflow?: { space?: DisplayableSpace | null } | null;
};

export type WorkflowSpaceLookup = ReadonlyMap<
  string,
  { space?: DisplayableSpace | null }
>;

export type ConnectorProviderMapping = {
  aiProviderId: string | null;
  providerType: string;
};

const CONNECTOR_TOKEN_FIELD = {
  input_tokens: "inputTokens",
  output_tokens: "outputTokens",
  cache_read_tokens: "cacheReadTokens",
  cache_write_tokens: "cacheWriteTokens",
} as const;

function isAiConnector(
  connectorId: string,
  connectorProviders: ReadonlyMap<string, ConnectorProviderMapping>
): boolean {
  return (
    isManagedAiConnectorId(connectorId) || connectorProviders.has(connectorId)
  );
}

function connectorProviderFor(
  connectorId: string,
  connectorProviders: ReadonlyMap<string, ConnectorProviderMapping>
): ConnectorProviderMapping {
  if (isManagedAiConnectorId(connectorId)) {
    return {
      aiProviderId: null,
      providerType:
        managedAiProviderFromConnectorId(connectorId) ??
        defaultManagedAIProvider.provider,
    };
  }
  // AI reached through a plain connector has no configured AI provider, so it
  // keeps its own connector id as the grouping key — it lands under "All
  // providers" without being mistaken for the managed provider (null id).
  return (
    connectorProviders.get(connectorId) ?? {
      aiProviderId: connectorId,
      providerType: "",
    }
  );
}

// connector_usage_daily is the source of truth for AI spend: it captures both
// platform AI calls and AI reached through a connector. Its narrow
// measure/dimension rows are pivoted back into the wide per-(connector, model)
// records the spend aggregations expect: token measures carry the model as their
// dimension, while the `requests` transport measure has no model, so it becomes
// a model-less turn-count record. Requests are only trustworthy as AI turns for
// connectors we know are AI providers — a plain connector's requests mix AI and
// non-AI HTTP calls — so they're dropped for everything else.
export function adaptConnectorUsageToAiRecords(
  rows: readonly ConnectorUsageDailyRow[],
  connectorProviders: ReadonlyMap<string, ConnectorProviderMapping>,
  workflowById?: WorkflowSpaceLookup
): AiUsageRecord[] {
  const byKey = new Map<string, AiUsageRecord>();
  for (const row of rows) {
    const field =
      CONNECTOR_TOKEN_FIELD[row.measure as keyof typeof CONNECTOR_TOKEN_FIELD];
    const isRequests =
      row.measure === "requests" &&
      isAiConnector(row.connectorId, connectorProviders);
    if (!field && !isRequests) continue;
    const model = row.dimension ?? "";
    const source = row.source ?? "";
    const workflowId = row.workflowId ?? null;
    const key = `${row.connectorId}\0${workflowId ?? ""}\0${model}\0${source}`;
    let record = byKey.get(key);
    if (!record) {
      const mapping = connectorProviderFor(row.connectorId, connectorProviders);
      record = {
        source,
        provider: mapping.providerType,
        model,
        aiProviderId: mapping.aiProviderId,
        connectorId: row.connectorId,
        workflowId,
        inputTokens: 0,
        outputTokens: 0,
        cacheReadTokens: 0,
        cacheWriteTokens: 0,
        requestCount: 0,
        workflow: (workflowId ? workflowById?.get(workflowId) : null) ?? null,
      };
      byKey.set(key, record);
    }
    if (field) record[field] = (record[field] ?? 0) + (row.value ?? 0);
    else record.requestCount = (record.requestCount ?? 0) + (row.value ?? 0);
  }
  return [...byKey.values()];
}

export type ModelUsageRow = {
  model: string;
  provider: string;
  aiProviderId: string | null;
  connectorId: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
};

export type AiUsageSummary = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
  modelCount: number;
  workflowCount: number;
};

export function recordTokens(record: AiUsageRecord) {
  return {
    inputTokens: record.inputTokens ?? 0,
    outputTokens: record.outputTokens ?? 0,
    cacheReadTokens: record.cacheReadTokens ?? 0,
    cacheWriteTokens: record.cacheWriteTokens ?? 0,
  };
}

export function recordCost(
  record: AiUsageRecord,
  prices?: ReadonlyMap<string, number>
): number | null {
  if (!prices || !record.connectorId || !record.model) return null;
  return estimateTokenCostUsd(
    record.connectorId,
    record.model,
    recordTokens(record),
    prices
  );
}

export function computeAiUsageSummary(
  records: readonly AiUsageRecord[],
  prices?: ReadonlyMap<string, number>
): AiUsageSummary {
  const models = new Set<string>();
  const workflows = new Set<string>();
  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let requestCount = 0;
  let estimatedCostUsd = 0;
  let hasAnyCost = false;

  for (const record of records) {
    requestCount += record.requestCount ?? 0;
    if (record.workflowId) workflows.add(record.workflowId);
    // Request-only rows (the connector `requests` measure) carry no model or
    // tokens, so they contribute turns but nothing to the token/cost totals.
    if (!record.model) continue;

    const tokens = recordTokens(record);
    inputTokens += tokens.inputTokens;
    outputTokens += tokens.outputTokens;
    cacheReadTokens += tokens.cacheReadTokens;
    models.add(record.model);

    const cost = recordCost(record, prices);
    if (cost != null) {
      estimatedCostUsd += cost;
      hasAnyCost = true;
    }
  }

  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    cacheReadTokens,
    requestCount,
    estimatedCostUsd: hasAnyCost ? estimatedCostUsd : null,
    modelCount: models.size,
    workflowCount: workflows.size,
  };
}

export function computeModelUsageRows(
  records: readonly AiUsageRecord[],
  prices?: ReadonlyMap<string, number>
): ModelUsageRow[] {
  const byModel = new Map<string, ModelUsageRow>();

  for (const record of records) {
    if (!record.model) continue;
    const key = `${record.provider}:${record.model}`;
    const tokens = recordTokens(record);
    const existing = byModel.get(key);
    if (existing) {
      existing.inputTokens += tokens.inputTokens;
      existing.outputTokens += tokens.outputTokens;
      existing.totalTokens += tokens.inputTokens + tokens.outputTokens;
      existing.cacheReadTokens += tokens.cacheReadTokens;
      existing.requestCount += record.requestCount ?? 0;
      if (existing.aiProviderId == null && record.aiProviderId != null) {
        existing.aiProviderId = record.aiProviderId;
        existing.connectorId = record.connectorId ?? null;
      }
      const cost = recordCost(record, prices);
      if (cost != null) {
        existing.estimatedCostUsd = (existing.estimatedCostUsd ?? 0) + cost;
      }
      continue;
    }

    const cost = recordCost(record, prices);
    byModel.set(key, {
      model: record.model,
      provider: record.provider,
      aiProviderId: record.aiProviderId ?? null,
      connectorId: record.connectorId ?? null,
      inputTokens: tokens.inputTokens,
      outputTokens: tokens.outputTokens,
      totalTokens: tokens.inputTokens + tokens.outputTokens,
      cacheReadTokens: tokens.cacheReadTokens,
      requestCount: record.requestCount ?? 0,
      estimatedCostUsd: cost,
    });
  }

  return [...byModel.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || a.model.localeCompare(b.model)
  );
}

export function formatTokenCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 10_000) {
    return `${Math.round(count / 1000)}K`;
  }
  return count.toLocaleString("en-US");
}

export function formatCacheHitRate(
  cacheReadTokens: number,
  totalInputTokens: number
): string {
  if (totalInputTokens === 0) return "—";
  return `${Math.round((cacheReadTokens / totalInputTokens) * 100)}%`;
}

export function formatEstimatedCostUsd(amount: number | null): string {
  if (amount == null) return "—";
  if (amount >= 100)
    return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (amount >= 10)
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
